import { useMemo } from 'react';
import { CustomizerMenuType } from './constants';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import {
  selectSpeakerAnalysis,
  selectSpeakerDetails
} from '@/player-config/src/selectors/contentMetadataStoreSelectors';
import { isSpeakerLayout } from '@/player-config/src/utils/layout';
import { getClipBroadcastId } from '@/player-config/src/utils/clipUtils';
import { isLayoutsWithVisionDataEnabled } from '@/stores/clip';

export enum CLIP_CUSTOMIZER_WARNING_TYPE {
  SPEAKER_FACE_MAPS_INCOMPLETE = 'Some speakers may not have been mapped.'
}

export interface CLIP_CUSTOMIZER_WARNING {
  showWarning: boolean;
  warnings: Partial<Record<CLIP_CUSTOMIZER_WARNING_TYPE, string>>;
}

const defaultWarnings: Record<CustomizerMenuType, CLIP_CUSTOMIZER_WARNING> = {
  [CustomizerMenuType.Layout]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Templates]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Translation]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.AI]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Speakers]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Captions]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Background]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Images]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Videos]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.Audio]: { showWarning: false, warnings: {} },
  [CustomizerMenuType.PromptOverride]: { showWarning: false, warnings: {} }
};

function useClipCustomizerWarnings() {
  const { clipData } = useClipsContext();
  const clipBroadcastId = getClipBroadcastId(clipData);
  const speakersWithDetails = useContentMetadataStore(selectSpeakerDetails(clipBroadcastId ?? ''));
  const speakersAnalysisForClip = useContentMetadataStore(selectSpeakerAnalysis(clipBroadcastId ?? ''));

  const layoutWarnings = useMemo(() => {
    if (!isLayoutsWithVisionDataEnabled(clipData)) {
      return null;
    }

    if (!speakersAnalysisForClip?.accurate) {
      return null;
    }

    if (
      !(clipData.asset_metadata?.scenes && clipData.asset_metadata.scenes.length > 1) &&
      !isSpeakerLayout(clipData.asset_metadata.layout)
    ) {
      return null;
    }

    // check if there are speakers that don't have face_ids in the speakersAnalysis
    const speakersWithoutFaceIds = Object.values(speakersWithDetails).filter(
      speaker => !speakersAnalysisForClip.speaker_mapping[speaker.id]
    );

    const mappedFaceIds = new Set(Object.values(speakersAnalysisForClip.speaker_mapping).flat());
    const allFaceIdsMapped = speakersAnalysisForClip.face_ids.every(faceId => mappedFaceIds.has(faceId));

    const speakerFaceMapsIncomplete =
      speakersAnalysisForClip.face_ids.length > 0 && speakersWithoutFaceIds.length > 0 && !allFaceIdsMapped;

    return {
      showWarning: speakerFaceMapsIncomplete,
      warnings: {
        ...(speakerFaceMapsIncomplete && {
          [CLIP_CUSTOMIZER_WARNING_TYPE.SPEAKER_FACE_MAPS_INCOMPLETE]:
            CLIP_CUSTOMIZER_WARNING_TYPE.SPEAKER_FACE_MAPS_INCOMPLETE.toString()
        })
      }
    };
  }, [clipData, speakersAnalysisForClip, speakersWithDetails]);

  return {
    ...defaultWarnings,
    ...(layoutWarnings && { [CustomizerMenuType.Layout]: layoutWarnings })
  };
}

export default useClipCustomizerWarnings;
